import { z } from 'zod';
import { defineCallable } from './callable-contract';
import { SITUATIONS } from './situations';
import { URGENCY_OPTIONS } from './urgency-options';

// ── ID types derived from SITUATIONS constant ────────────────────────────────

type SituationId = (typeof SITUATIONS)[number]['id'];
type MatterId = (typeof SITUATIONS)[number]['matters'][number]['id'];
type AreaId = (typeof SITUATIONS)[number]['areas'][number];
type UrgencyId = (typeof URGENCY_OPTIONS)[number]['id'];

const situationIds = SITUATIONS.map((s) => s.id) satisfies readonly SituationId[];
const matterIds = SITUATIONS.flatMap((s) =>
  s.matters.map((m) => m.id),
) satisfies readonly MatterId[];
const areaIds = [...new Set(SITUATIONS.flatMap((s) => s.areas))] satisfies readonly AreaId[];
const urgencyIds = URGENCY_OPTIONS.map((u) => u.id) satisfies readonly UrgencyId[];

const SituationIdSchema = z.enum(situationIds as [SituationId, ...SituationId[]]);
const MatterIdSchema = z.enum(matterIds as [MatterId, ...MatterId[]]);
const AreaIdSchema = z.enum(areaIds as [AreaId, ...AreaId[]]);
const UrgencyIdSchema = z.enum(urgencyIds as [UrgencyId, ...UrgencyId[]]);

// ── Situation / Matter / Urgency schemas ─────────────────────────────────────

export const SituationSchema = z.object({
  id: SituationIdSchema,
  emoji: z.string(),
  label: z.string(),
  sub: z.string(),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
  areas: z.array(AreaIdSchema).readonly(),
});

export const MatterSchema = z.object({
  id: MatterIdSchema,
  label: z.string(),
  formal: z.string(),
});

export const UrgencySchema = z.object({
  id: UrgencyIdSchema,
  label: z.string(),
  icon: z.string(),
  target: z.string(),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
});

export const SelectedSituationSchema = SituationSchema.nullable();
export const SelectedMatterSchema = MatterSchema.nullable();
export const SelectedUrgencySchema = UrgencySchema.nullable();

// ── Contact fields (shared by all channels) ──────────────────────────────────

export const ContactFieldsSchema = z.object({
  name: z.string().nullable(),
  email: z.string().email().nullable(),
  phone: z.string().nullable(),
});
export type ContactFields = z.infer<typeof ContactFieldsSchema>;

// ── Submission schemas by channel ────────────────────────────────────────────

/** Web form submissions include situation/matter/urgency context. */
export const IntakePayloadSchema = z.strictObject({
  situation: SelectedSituationSchema,
  matter: SelectedMatterSchema,
  urgency: SelectedUrgencySchema,
  qualifiers: z.record(z.string(), z.unknown()).nullish(),
  name: z.string().min(1, 'Name is required').nullable(),
  phone: z.string().min(1, 'Phone number is required').nullable(),
  email: z.email('Invalid email address').nullable(),
  referralSource: z.string().nullish(),
});
export type IntakePayload = z.infer<typeof IntakePayloadSchema>;

/** Email intake submissions have contact fields only. */
export const EmailSubmissionSchema = ContactFieldsSchema;
export type EmailSubmission = z.infer<typeof EmailSubmissionSchema>;

/**
 * Submission field on IntakeSession — either a full web form payload
 * or contact-only fields from email intake.
 */
export const IntakeSubmissionSchema = z
  .union([IntakePayloadSchema, ContactFieldsSchema])
  .nullable();
export type IntakeSubmission = z.infer<typeof IntakeSubmissionSchema>;

// ── Email intake metadata ────────────────────────────────────────────────────

export const EmailIntakeSchema = z.object({
  fromAddress: z.string(),
  fromName: z.string(),
  subject: z.string(),
  rawText: z.string(),
  rawHtml: z.string().optional(),
  messageId: z.string(),
  phoneSource: z.enum(['extracted']).nullable(),
  multiplePhones: z.boolean(),
  lowConfidence: z.boolean(),
  softSignals: z.array(z.string()),
  wasForwarded: z.boolean(),
});
export type EmailIntake = z.infer<typeof EmailIntakeSchema>;

// ── Manual intake (firm-user-initiated) ─────────────────────────────────────

export const ManualIntakePayloadSchema = z
  .object({
    organizationId: z.string().min(1),
    channel: z.enum(['phone', 'email', 'walk_in', 'referral']),
    prospectName: z.string().min(2).max(200),
    prospectEmail: z.email().max(254).nullish(),
    prospectPhone: z.string().max(30).nullish(),
    situation: SelectedSituationSchema,
    matter: SelectedMatterSchema,
    urgency: SelectedUrgencySchema,
    qualifiers: z.record(z.string(), z.unknown()).nullish(),
    notes: z.string().max(2000).nullish(),
    referralSource: z.string().max(500).nullish(),
    sendConfirmation: z.boolean(),
  })
  .refine((d) => d.prospectEmail || d.prospectPhone, {
    message: 'At least one contact method required',
    path: ['prospectEmail'],
  })
  .refine((d) => d.channel !== 'referral' || d.referralSource, {
    message: 'Referral source required for referral channel',
    path: ['referralSource'],
  });
export type ManualIntakePayload = z.infer<typeof ManualIntakePayloadSchema>;

export const ManualIntakeResponseSchema = z.object({
  ok: z.boolean(),
  sessionId: z.string(),
});
export type ManualIntakeResponse = z.infer<typeof ManualIntakeResponseSchema>;

export const manualIntakeContract = defineCallable('manualIntake', {
  input: ManualIntakePayloadSchema,
  output: ManualIntakeResponseSchema,
});
